// Conversation Monitor Component
// Phase 10: Admin Dashboard
// © 2025 precatio, llc

import React, { useState, useEffect } from 'react';

const ConversationMonitor = ({ token }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    fetchConversations();
  }, [limit]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/conversations?limit=${limit}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getConversationTypeLabel = (type) => {
    const labels = {
      examen: 'Daily Examen',
      prayers: 'Common Prayers',
      novena: 'Custom Novena',
      gospelprayer: 'Gospel Prayer',
      askiggy: 'Ask Iggy',
      discernment: 'Discernment'
    };
    return labels[type] || type;
  };

  const getTierColor = (tier) => {
    const colors = {
      pilgrim: '#9e9e9e',
      disciple: '#d4af37',
      companion: '#9c27b0',
      retreatant: '#9c27b0'
    };
    return colors[tier] || '#9e9e9e';
  };

  const getTierLabel = (tier) => {
    const labels = {
      pilgrim: 'Pilgrim',
      disciple: 'Disciple',
      companion: 'Companion',
      retreatant: 'Retreatant'
    };
    return labels[tier] || tier;
  };

  return (
    <div className="conversation-monitor">
      <div className="stats-header">
        <h2>Recent Conversations</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            className="user-filter"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
          >
            <option value="25">Show 25</option>
            <option value="50">Show 50</option>
            <option value="100">Show 100</option>
          </select>
          <button onClick={fetchConversations} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">Loading conversations...</div>
      ) : conversations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#a0a0a0' }}>
          No conversations found
        </div>
      ) : (
        <div className="conversation-list">
          {conversations.map((conv) => (
            <div key={conv.session_id} className="conversation-item">
              <div className="conversation-header">
                <span className="conversation-type">
                  {getConversationTypeLabel(conv.conversation_type)}
                </span>
                <span className="conversation-time">
                  {formatTimeAgo(conv.last_message)}
                </span>
              </div>
              <div className="conversation-details">
                <span>
                  <strong>User:</strong> {conv.email}
                </span>
                <span>
                  <strong>Tier:</strong>{' '}
                  <span style={{ color: getTierColor(conv.subscription_tier) }}>
                    {getTierLabel(conv.subscription_tier)}
                  </span>
                </span>
                <span>
                  <strong>Messages:</strong> {conv.message_count}
                </span>
                <span>
                  <strong>Session:</strong> {conv.session_id.substring(0, 8)}...
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConversationMonitor;
